import * as React from "react";
import { CheckIcon } from "@heroicons/react/solid";

interface Handles {
  nextStep: () => string;
  prevStep: () => string;
}

interface IProps {
  show_caddies: boolean;
  show_carts: boolean;
  show_transfers: boolean;
  show_golfer_info: boolean;
  active_step: string;
  onStepChange: (step_id: string) => void;
}

const Stepper = React.forwardRef<Handles, IProps>(
  (
    {
      show_caddies,
      show_carts,
      show_transfers,
      show_golfer_info,
      active_step,
      onStepChange,
    },
    ref
  ) => {
    React.useImperativeHandle(ref, () => {
      return {
        nextStep: () => {
          return getNextStep();
        },
        prevStep: () => {
          return getPrevStep();
        },
      };
    });

    const steps = React.useMemo(() => {
      const arry = [
        { id: "tee_time", name: "Tee time", href: "#", status: "upcoming" },
      ];

      if (show_caddies) {
        arry.push({
          id: "caddies",
          name: "Caddies",
          href: "#",
          status: "upcoming",
        });
      }
      if (show_carts) {
        arry.push({
          id: "carts",
          name: "Carts",
          href: "#",
          status: "upcoming",
        });
      }
      if (show_golfer_info) {
        arry.push({
          id: "golfer_info",
          name: "Golfer info",
          href: "#",
          status: "upcoming",
        });
      }
      if (show_transfers) {
        arry.push({
          id: "transfers",
          name: "Transfers",
          href: "#",
          status: "upcoming",
        });
      }
      arry.push({
        id: "review",
        name: "Review",
        href: "#",
        status: "upcoming",
      });

      // go through each item in array and set the status based on the active_step
      arry.forEach((step, stepIdx) => {
        if (step.id === active_step) {
          step.status = "current";
        } else if (stepIdx < arry.findIndex((s) => s.id === active_step)) {
          step.status = "complete";
        }
      });

      return arry;
    }, [
      show_caddies,
      show_carts,
      show_transfers,
      show_golfer_info,
      active_step,
    ]);

    const goToStep = React.useCallback(
      (step_id: string) => {
        // get the index of the current step
        const current_step_idx = steps.findIndex((s) => s.id === active_step);

        // get the index of the step we want to go to
        const step_idx = steps.findIndex((s) => s.id === step_id);

        // if the step we want to go to is before the current step, go back
        if (step_idx < current_step_idx) {
          if (onStepChange) onStepChange(steps[step_idx].id);
        }
      },
      [steps]
    );

    const getNextStep = React.useCallback(() => {
      // get the index of the current step
      const current_step_idx = steps.findIndex((s) => s.id === active_step);

      // if the current step is the last step, do nothing
      if (current_step_idx === steps.length - 1) return;

      // get the next step
      const next_step = steps[current_step_idx + 1];

      // set the current step
      return next_step.id;
    }, [steps]);

    const getPrevStep = React.useCallback(() => {
      // get the index of the current step
      const current_step_idx = steps.findIndex((s) => s.id === active_step);

      // if the current step is the first step, do nothing
      if (current_step_idx === 0) return;

      // get the previous step
      const prev_step = steps[current_step_idx - 1];

      // set the current step
      return prev_step.id;
    }, [steps]);

    return (
      <div className="px-0 ">
        <div className="border-l-0 border-r-0 md:border-r md:border-l border border-grey2 py-3 px-8 md:rounded-md mb-8 ">
          <div className="flex items-center mb-5">
            {steps.map((step, stepIdx) => {
              // only show if not the first or last step
              let showBar = stepIdx === 0 || stepIdx === steps.length;
              return (
                <React.Fragment key={stepIdx}>
                  {step.status === "complete" ? (
                    <>
                      {!showBar && (
                        <div className="flex-auto border-t transition duration-500 ease-in-out border-blue"></div>
                      )}
                      <div
                        className="flex items-center text-blue relative cursor-pointer"
                        onClick={() => {
                          goToStep(step.id);
                        }}
                      >
                        <div className="rounded-full transition duration-500 ease-in-out h-5 w-5 bg-blue border border-blue flex justify-center text-white text-10 leading-[2em]">
                          <CheckIcon className="p-[2px]" />
                        </div>
                        <div className="absolute top-0 ml-[-22px] text-center mt-5 w-16 text-xs font-medium text-gray-500">
                          <span className="text-11">{step.name}</span>
                        </div>
                      </div>
                    </>
                  ) : step.status === "current" ? (
                    <>
                      {!showBar && (
                        <div className="flex-auto border-t transition duration-500 ease-in-out border-blue"></div>
                      )}{" "}
                      <div className="flex items-center text-blue relative">
                        <div className="font-semibold rounded-full transition duration-500 ease-in-out h-5 w-5  border border-blue flex justify-center text-10 leading-[2em]">
                          {stepIdx + 1}
                        </div>
                        <div className="absolute top-0 ml-[-22px] text-center mt-5 w-16 text-xs font-medium text-blue">
                          <span className="text-11 font-semibold">
                            {step.name}
                          </span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {!showBar && (
                        <div className="flex-auto border-t transition duration-500 ease-in-out border-grey3"></div>
                      )}{" "}
                      <div className="flex items-center text-grey3 relative">
                        <div className="font-semibold rounded-full transition duration-500 ease-in-out h-5 w-5  border border-grey3 flex justify-center text-10 leading-[2em]">
                          {stepIdx + 1}
                        </div>
                        <div className="absolute top-0 ml-[-22px] text-center mt-5 w-16 text-xs font-medium text-grey3">
                          <span className="text-11">{step.name}</span>
                        </div>
                      </div>
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
);

export default Stepper;
